import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Typography } from "@mui/material";
import { IoCall } from "react-icons/io5";
import { FaMapMarkerAlt } from "react-icons/fa";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

export default function Map({
  addresses,
  highLighData,
  userCurrentLocation,
  setUserCurrentLocation,
}) {
  const navigate = useNavigate();
  const [userLocation] = useState([34.0522, -118.2437]);
  const [duplicateAddresses, setDuplicateAddresses] = useState([]);

  useEffect(() => {
    const tooltipTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.map((tooltipTriggerEl) => {
      return new window.bootstrap.Tooltip(tooltipTriggerEl);
    });

    const addressCount = {};
    const duplicates = [];

    addresses?.forEach((addr) => {
      const fullAddress = `${addr.address}, ${addr.city}, ${addr.state}, ${addr.zip}`;
      if (addressCount[fullAddress]) {
        duplicates.push(addr);
      } else {
        addressCount[fullAddress] = true;
      }
    });

    setDuplicateAddresses(duplicates);
  }, [addresses]);

  const validAddresses = addresses
    ?.map((addr) => ({
      ...addr,
      lat: Number(addr?.x),
      lng: Number(addr?.y),
    }))
    .filter((addr) => !isNaN(addr.lat) && !isNaN(addr.lng));

  const handleMarkerClick = (address) => {
    highLighData(address);
  };

  const handleCompanyNameClick = (e, companyData) => {
    e.preventDefault();
    navigate("/company-detail", { state: { data: companyData } });
  };

  // Helper function to check if a company is unverified/unclaimed
  // Show unclaimed indicators for all users (logged in and not logged in)
  const isUnverifiedCompany = (company) => {
    // Company is unverified if admin_verified is null/undefined OR company is not verified by email (is_verified_by_email is false)
    return (
      company.admin_verified === null ||
      company.admin_verified === undefined ||
      company.is_verified_by_email === false ||
      !company.is_verified_by_email
    );
  };

  function KeepAllPointsCentered() {
    const map = useMap();
    useEffect(() => {
      // debugger
      const bounds = L?.latLngBounds(
        validAddresses?.map((addr) => [addr.lat, addr.lng])
      );
      console.log('bounds',bounds);
      
      if (Object.keys(bounds).length) {
        map?.fitBounds(bounds);
      }
    }, [map, validAddresses]);
    return null;
  }

  const createCustomMarker = (number) => {
    return L.divIcon({
      html: `<div style="
          background-color: #2b83ba;
          color: white;
          width: 30px;
          height: 30px;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          text-align: center;
          line-height: 30px;
          font-weight: bold;
          border: 2px solid white;
          font-size: 14px;">
          <div style="transform: rotate(45deg)!important;">
          ${number}
          </div>
        </div>`,
      className: "",
    });
  };

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserCurrentLocation({ x: latitude, y: longitude });
        },
        (error) => {
          console.error(error);
          setUserCurrentLocation({ x: -1, y: -1 });
        }
      );
    }
  }, []);

  return (
    <div style={{ height: "350px", position: 'relative', zIndex: '0' }} className="map-con">
      <MapContainer
        center={userLocation}
        zoom={6}
        style={{ width: "100%", height: "100%", boxShadow: '0 4px 12px rgba(27,71,102,0.15)' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        <KeepAllPointsCentered />

        {validAddresses?.map((address, index) => {
          // Check if company is unclaimed/unverified
          const isUnverified = isUnverifiedCompany(address);
          const companyColor = isUnverified ? "#FFD700" : "#A5BF45"; // Yellow for unclaimed, green for verified

          return (
          <Marker
            key={index}
            position={[address.lat, address.lng]}
            icon={createCustomMarker(index + 1)}
            eventHandlers={{
              click: () =>
                handleMarkerClick(
                  `${address.address}, ${address.city}, ${address.state}, ${address.zip}`
                ),
            }}
          >
            <Popup>
              <Typography
                style={{ 
                  margin: "1.5em 0em 0.5em 0em", 
                  fontWeight: "bold",
                  cursor: "pointer",
                  textDecoration: "underline",
                  color: companyColor
                }}
                onClick={(e) => handleCompanyNameClick(e, address)}
              >
                {address.name}
              </Typography>

              <Typography style={{ margin: "0em " }}>
                <FaMapMarkerAlt /> {address.address}
                <br />
                {address.city && `${address.city}, `}
                {address.state && `${address.state}, `}
                {address.zip && `${address.zip}`}
              </Typography>
              {address.phone && (
                <Typography style={{ margin: "0.5em 0em 0.5em 0em" }}>
                  <IoCall /> {`${address.phone}`}
                </Typography>
              )}
            </Popup>
          </Marker>
          );
        })}
      </MapContainer>

      {/* Display overlay when duplicate addresses are found */}
      {duplicateAddresses.length > 0 && (
        <div
          style={{
            display: "flex",
            width: "100%",
            alignItems: "center",
            height: "2.5em",
            backgroundColor: "#eaeaea",
            justifyContent: "center",
          }}
        >
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="tooltip-top">
                Some providers are located in the same building. This causes their
                map markers to stack. Click a map marker in the results to bring
                it to the front.
              </Tooltip>
            }
          >
            <a href="#" style={{ textDecoration: "none", color: "black" }}>
              Why are markers missing?
            </a>
          </OverlayTrigger>
        </div>
      )}
    </div>
  );
}
